/**
 * Минимальный markdown-рендер для сообщений AI-ассистента.
 *
 * Исходный текст сначала экранируется (безопасно для v-html), затем применяются
 * простые подстановки: код-блоки ```, инлайн-код `, **жирный**, *курсив*, _курсив_,
 * ссылки [текст](http...), списки (- / 1.), заголовки #..####, GFM-таблицы (| ... |).
 * Внешний markdown-пакет не тащим: для чат-панели хватает этого подмножества.
 */

const CODE_PLACEHOLDER = '%%MDCODE';

const escapeHtml = (text) =>
    String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const renderInline = (text) => {
    let result = text;
    // Инлайн-код
    result = result.replace(/`([^`\n]+)`/g, '<code>$1</code>');
    // Жирный
    result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Курсив (не задевать уже обработанный жирный)
    result = result.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
    result = result.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, '$1<em>$2</em>');
    // Ссылки — только http(s)
    result = result.replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    return result;
};

// Строка-разделитель таблицы: |---|---|, допускаются двоеточия выравнивания
const isTableSeparator = (line) => {
    const trimmed = line.trim();
    return trimmed.includes('|') && trimmed.includes('-') && /^\|?[\s:|-]+\|?$/.test(trimmed);
};

// «| a | b |» / «a | b» → ['a', 'b']
const splitTableRow = (line) =>
    line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());

export function renderMarkdown(text) {
    if (!text) return '';

    // Код-блоки вырезаем до всей разметки, чтобы не трогать их содержимое
    const codeBlocks = [];
    const source = String(text).replace(/```[a-z]*\n?([\s\S]*?)```/g, (match, code) => {
        codeBlocks.push(code.replace(/\n$/, ''));
        return CODE_PLACEHOLDER + (codeBlocks.length - 1) + '%%';
    });

    const html = [];
    let listType = null; // 'ul' | 'ol' | null

    const closeList = () => {
        if (listType) {
            html.push(`</${listType}>`);
            listType = null;
        }
    };

    const lines = escapeHtml(source).split('\n');
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        if (trimmed === '') {
            closeList();
            continue;
        }

        // Таблица: строка заголовка + строка-разделитель на следующей строке
        if (trimmed.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
            closeList();
            const header = splitTableRow(line);
            i += 2; // разделитель пропускаем
            const rows = [];
            while (i < lines.length && lines[i].trim() !== '' && lines[i].trim().includes('|')) {
                rows.push(splitTableRow(lines[i]));
                i++;
            }
            i--; // шаг назад: цикл for сам увеличит счётчик
            html.push(
                '<table class="md-table"><thead><tr>'
                + header.map((cell) => '<th>' + renderInline(cell) + '</th>').join('')
                + '</tr></thead><tbody>'
                + rows.map((row) =>
                    '<tr>' + row.map((cell) => '<td>' + renderInline(cell) + '</td>').join('') + '</tr>'
                ).join('')
                + '</tbody></table>'
            );
            continue;
        }

        const headerMatch = trimmed.match(/^(#{1,4})\s+(.*)$/);
        if (headerMatch) {
            closeList();
            html.push('<div class="md-header">' + renderInline(headerMatch[2]) + '</div>');
            continue;
        }

        const ulMatch = trimmed.match(/^[-*]\s+(.*)$/);
        if (ulMatch) {
            if (listType !== 'ul') {
                closeList();
                html.push('<ul>');
                listType = 'ul';
            }
            html.push('<li>' + renderInline(ulMatch[1]) + '</li>');
            continue;
        }

        const olMatch = trimmed.match(/^\d+[.)]\s+(.*)$/);
        if (olMatch) {
            if (listType !== 'ol') {
                closeList();
                html.push('<ol>');
                listType = 'ol';
            }
            html.push('<li>' + renderInline(olMatch[1]) + '</li>');
            continue;
        }

        closeList();
        html.push('<div>' + renderInline(line) + '</div>');
    }
    closeList();

    // Восстанавливаем код-блоки по плейсхолдерам
    return html
        .join('')
        .replace(/%%MDCODE(\d+)%%/g, (match, index) =>
            '<pre><code>' + escapeHtml(codeBlocks[Number(index)]) + '</code></pre>'
        );
}

export default { renderMarkdown };
